import { z } from "zod";
import { TemplateModuleSchema } from "./form-module.js";
import { ProgramSummarySchema } from "./program.js";
import { ServiceTypeSummarySchema } from "./service-types.js";
// A named, ordered bundle of session-note modules (ADR-032). A practitioner
// opening a session is served the template matching that session's programme and
// service type; several may match, and the practitioner is asked which they want
// (Relationships-Australia-WA/Services-Delivery-Technology-Upgrade#427), so a
// template being for a programme or service type stops no other being for it.
export const SessionTemplateSchema = z.object({
    id: z.guid(),
    name: z.string(),
    description: z.string(),
    // The programmes this template is for, by name. Empty is a real state: a
    // template may be set up before anybody decides which programmes it serves.
    programs: ProgramSummarySchema.array(),
    // The service types whose sessions it is for, on the same terms.
    serviceTypes: ServiceTypeSummarySchema.array(),
});
export const SessionTemplatesResponseSchema = z.object({
    sessionTemplates: SessionTemplateSchema.array(),
});
// One template with the modules it is built from, in the order a note shows
// them, each at the version the template pins. The list does not carry these,
// so the detail shape is its own schema rather than a wider
// SessionTemplateSchema.
export const SessionTemplateDetailSchema = SessionTemplateSchema.extend({
    modules: TemplateModuleSchema.array(),
    // Counts the changes to what the template's notes collect, from 1 at
    // creation. Shown to administrators (ADR-032 O).
    version: z.int().positive(),
    // Counts the template's saves from 1, so a save can say which one it was
    // edited from, as a module's and a session note's revision do.
    revision: z.int().positive(),
});
// The write body for POST /api/admin/session-templates. Trimmed here rather than
// in the handler: the name's uniqueness index is on lower(name), so surrounding
// whitespace has to be gone before the database decides whether it is a
// duplicate. The id is the server's to supply.
export const CreateSessionTemplateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2000),
    // Which programmes and service types the template is for. Optional on the
    // wire and empty by default, because a template may be set up before either
    // is decided.
    programIds: z.guid().array().default([]),
    serviceTypeIds: z.guid().array().default([]),
});
// Whether a name is free, for the builder to warn as it is typed. Advisory: the
// unique index is what refuses a duplicate on save.
export const SessionTemplateNameAvailabilitySchema = z.object({
    available: z.boolean(),
});
// The write body for PUT /api/admin/session-templates/:id: the complete desired
// state of the template, so what is absent is absent, not unchanged. Trimmed
// for the same reason as on create.
export const UpdateSessionTemplateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2000),
    programIds: z.guid().array(),
    serviceTypeIds: z.guid().array(),
    // The modules the template is built from, in order.
    moduleIds: z.guid().array(),
    // Not part of the template as it is to be: a check on it. The revision the
    // save was edited from; one that is no longer current is refused,
    // SESSION_TEMPLATE_CHANGED, so a save cannot put back a change it never saw.
    revision: z.int().positive(),
});
// What a client is told about a template write beyond its status, as an
// error's code (ADR-033 rule 7): a save made from a revision someone has
// saved past sends the administrator back to reload.
export const SessionTemplateErrorCodeSchema = z.enum([
    "SESSION_TEMPLATE_CHANGED",
]);
export const SessionTemplateErrorCode = SessionTemplateErrorCodeSchema.enum;
